import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  registerEmployee,
  editEmployee,
  deleteEmployee,
  getUser,
  getEmployeesAttendance,
  getEmployeeAddresses,
  getIndividualAttendance,
  getDepartments,
} from "../services/api";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";

export const useGetEmployees = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["get-employee"],
    queryFn: async () => {
      const response = await getUser();
      return response;
    },
  });

  const employee = data?.data?.data;

  return { employee, isPending, isError, error };
};
export const useRegisterEmployee = () => {
  const queryClient = useQueryClient();
  const {
    isPending,
    mutate: mutateRegisterEmployee,
    error,
    isError,
    isSuccess,
    data,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const response = await registerEmployee(payload);
      return response;
    },
    mutationKey: ["register-employee"],
    onSuccess: (data) => {
      if (data) {
        toast.success(data?.data?.message);
        queryClient.invalidateQueries({ queryKey: ["get-employee"] });
      }
    },
    onError: (error: { message: string }) => {
      toast.error(error.message || "Failed to create event");
    },
  });
  return {
    isPending,
    mutateRegisterEmployee,
    error,
    isError,
    isSuccess,
    data,
  };
};

export const useEditEmployee = () => {
  const queryClient = useQueryClient();
  const {
    isPending,
    mutate: mutateEditEmployee,
    error,
    isError,
    isSuccess,
    data,
  } = useMutation({
    mutationFn: (payload: any) => editEmployee(payload),
    mutationKey: ["edit-employee"],
    onSuccess(data: AxiosResponse) {
      toast.success(data?.data?.data?.message);
      queryClient.invalidateQueries({ queryKey: ["get-employee"] });
    },
    onError: (error: AxiosResponse) => {
      console.log(error);
      toast.error(error.data.message);
    },
  });
  return {
    isPending,
    mutateEditEmployee,
    error,
    isError,
    isSuccess,
    data,
  };
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  const {
    isPending,
    mutate: mutateDeleteEmployee,
    error,
    isError,
    isSuccess,
    data,
  } = useMutation({
    mutationFn: (payload: any) => deleteEmployee(payload),
    mutationKey: ["delete-employee"],
    onSuccess(data: AxiosResponse) {
      toast.success("Employee successfully deleted!");
      queryClient.invalidateQueries({ queryKey: ["get-employee"] });
    },
    onError: (error: AxiosResponse) => {
      console.log(error);
      toast.error("Account not deleted, please contact the HR.");
    },
  });
  return {
    isPending,
    mutateDeleteEmployee,
    error,
    isError,
    isSuccess,
    data,
  };
};

export const useGetEmployeesAttendance = (
  startDate: string,
  endDate: string
) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["get-employee-attendance", startDate, endDate],
    queryFn: async () => {
      const response = await getEmployeesAttendance(startDate, endDate);
      return response;
    },
    placeholderData: keepPreviousData,
  });

  const employee = data?.data?.data;

  return { employee, isPending, isError, error };
};

export const useGetEmployeeAddresses = (latitude: any, longitude: any) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["get-employee-addresses"],
    queryFn: async () => {
      const response = await getEmployeeAddresses(latitude, longitude);
      return response;
    },
  });

  const address = data?.data?.data;

  return { address, isPending, isError, error };
};

export const useGetIndividualAttendance = (user: number) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["get-individual-attendance", user],
    enabled: !!user,
    queryFn: async () => {
      const response = await getIndividualAttendance(user);
      return response;
    },
  });

  const logs = data?.data?.data;

  return { logs, isPending, isError, error };
};

export const useGetDepartments = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const response: any = await getDepartments();
      return response;
    },
  });

  const department = data?.data?.data;

  return { department, isPending, isError, error };
};
